import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateAIBrandProfile } from '@/hooks/useAIBrandProfiles';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import type { AIBrandProfile, AIBrandProfileFormData } from '@/types/ai';
import { DEFAULT_PROFILE_FORM_DATA } from '@/types/ai';
import { WizardModal } from '@/components/ui/WizardModal';
import {
  useBrandProfileWizardSteps,
  ProfileWizardContent,
  ProfileWizardFooter,
  mapFormDataToInput,
  mapProfileToFormData,
} from '@/components/settings/AIBrandProfileSettings';

/**
 * AIBrandProfileEditModal
 *
 * Shared modal for editing an existing AI Brand Profile, used by both the
 * Settings page and the Dashboard AIProfilesSection widget.
 *
 * No autosave for edit mode — the profile is already persisted on the server.
 * Close discards unsaved local edits (same behaviour as before).
 */

interface Notification {
  type: 'success' | 'error';
  message: string;
}

interface AIBrandProfileEditModalProps {
  /** The profile to edit, or null when modal is closed */
  profile: AIBrandProfile | null;
  /** Controlled open state */
  open: boolean;
  /** Called when the modal wants to open or close itself */
  onOpenChange: (open: boolean) => void;
  /** Called after a successful update mutation (receives the updated profile) */
  onSuccess?: (profile: AIBrandProfile) => void;
  /** Called to surface a notification toast in the parent */
  onNotify?: (notification: Notification) => void;
}

export const AIBrandProfileEditModal = ({
  profile,
  open,
  onOpenChange,
  onSuccess,
  onNotify,
}: AIBrandProfileEditModalProps) => {
  const { t } = useTranslation(['settings', 'common']);
  const { steps: wizardSteps, descriptions: stepDescriptions } = useBrandProfileWizardSteps();
  const [formData, setFormData] = useState<AIBrandProfileFormData>(DEFAULT_PROFILE_FORM_DATA);
  const [step, setStep] = useState(0);
  // Snapshot of the form data as originally loaded from the profile, used to
  // detect unsaved edits (see isDirty below).
  const [initialFormData, setInitialFormData] = useState<AIBrandProfileFormData>(DEFAULT_PROFILE_FORM_DATA);

  const updateMutation = useUpdateAIBrandProfile();

  // Populate form whenever the target profile changes or modal opens.
  useEffect(() => {
    if (open && profile) {
      const mapped = mapProfileToFormData(profile);
      setFormData(mapped);
      setInitialFormData(mapped);
      setStep(0);
    }
  }, [open, profile]);

  // Dirty when the in-progress form differs from the snapshot loaded on open.
  const isDirty = open && JSON.stringify(formData) !== JSON.stringify(initialFormData);

  // Register this editor's dirty state globally while it is open, so the
  // auto-reload mechanism defers to a manual refresh banner instead of
  // silently discarding in-progress edits.
  useUnsavedGuard(isDirty, 'ai-brand-profile-edit');

  const handleClose = () => {
    onOpenChange(false);
    setFormData(DEFAULT_PROFILE_FORM_DATA);
    setStep(0);
  };

  const handleSubmit = () => {
    if (!profile) return;

    if (!formData.name.trim()) {
      onNotify?.({ type: 'error', message: 'Brand World name is required' });
      return;
    }

    const input = mapFormDataToInput(formData);

    updateMutation.mutate(
      { id: profile.id, input },
      {
        onSuccess: (updatedProfile) => {
          onOpenChange(false);
          setFormData(DEFAULT_PROFILE_FORM_DATA);
          setStep(0);
          onSuccess?.(updatedProfile);
        },
      }
    );
  };

  return (
    <WizardModal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        } else {
          onOpenChange(true);
        }
      }}
      title={t('settings:settings_ai_wizard_title_edit')}
      size="xl"
      steps={wizardSteps}
      currentStep={step}
      onStepClick={(index) => setStep(index)}
      stepDescription={stepDescriptions[step]}
      footer={
        <ProfileWizardFooter
          formData={formData}
          onChange={setFormData}
          step={step}
          onStepChange={setStep}
          onSubmit={handleSubmit}
          onCancel={handleClose}
          isSubmitting={updateMutation.isPending}
          mode="edit"
        />
      }
    >
      <ProfileWizardContent formData={formData} onChange={setFormData} step={step} />
    </WizardModal>
  );
};
